import type { QueryClient } from '@tanstack/react-query';
import { invalidateOverrideSurfaces } from './override-invalidation';
import {
  DIALOG_SYNCED_DATE_FIELDS,
  MILESTONE_WIRE_FIELD,
} from './schedule-dialog/constants';
import type { SlotOverrideOperation } from './types';

/**
 * The date fields an override operation can carry that the dialog channel ALSO owns, in the
 * dialog's snake_case wire spelling — {@link DIALOG_SYNCED_DATE_FIELDS} composed onto
 * {@link MILESTONE_WIRE_FIELD}.
 *
 * Derived from that shared map rather than from {@link GRID_SYNCED_MILESTONES} because the set
 * the dialog owns is not the set the grid can originate: the tearsheet writes `completionDate`
 * into the same log, and without it here the dialog channel — applied LAST in both
 * overlayScheduleDateOverrides and the Calculate merge — silently reverts a tearsheet
 * completion edit back to the dialog's value. The map carries the completionDate →
 * modeledCompletionDate spelling difference that makes this a map and not a list; see its own
 * comment for why indexing `operation.overrides` by the milestone key prunes nothing.
 */
const PRUNABLE_DATE_FIELDS: Record<string, string> = Object.fromEntries(
  Object.entries(DIALOG_SYNCED_DATE_FIELDS).map(([field, milestone]) => [
    field,
    MILESTONE_WIRE_FIELD[milestone],
  ])
);

/**
 * Drops the dialog channel's (`scheduleSlotOverrides`) value for any editable milestone a fresh
 * grid edit just set, so a LATER grid edit wins over an earlier dialog value for the same
 * slot+field (the two-way sync). Without it the dialog channel is applied LAST — in both
 * {@link overlayScheduleDateOverrides} and the Calculate merge — silently reverting the grid edit
 * back to the dialog's value.
 *
 * One-sided by design: the dialog owns its channel and rebuilds it on flush, so the reverse
 * (dialog edited last) already wins via that last-applied precedence. Only `slot`/`ids` ops are
 * prunable; a `filter` (select-all) op carries no enumerated ids — the same limitation as
 * `deriveSlotDateOverrides` — so a filter-scoped date edit still can't beat a prior dialog value.
 * Completion IS pruned now that the tearsheet can edit it — see {@link PRUNABLE_DATE_FIELDS}.
 *
 * @param {Array<Record<string, unknown>>} scheduleSlotOverrides - The dialog channel (wire ops).
 * @param {SlotOverrideOperation} operation - The grid override just committed.
 * @return {Array<Record<string, unknown>>} The channel with the now-grid-owned fields dropped;
 *   the SAME reference when nothing changed, so the form field isn't spuriously dirtied.
 */
export function pruneScheduleOverridesForOperation(
  scheduleSlotOverrides: Array<Record<string, unknown>>,
  operation: SlotOverrideOperation
): Array<Record<string, unknown>> {
  // ── A REVERT PRUNES TOO ───────────────────────────────────────────────────────────────
  //
  // Because the dialog channel is applied last, a Revert on the tearsheet would not undo a
  // date set in the schedule dialog unless an unset also drops that slot's named fields from
  // the dialog list.
  //
  // An unset names its fields in `fields` rather than in `overrides`, and both kinds mean the
  // same thing to this function: the grid has just spoken about these slot+field pairs, so
  // the dialog's older copy of them must not be applied over the top. Taking only the SET
  // half left a revert visibly undone — the value returned a moment later, from the channel
  // that is applied last.
  // `!== undefined` on the SET half, exactly as before: a key present with an undefined value
  // names nothing, and treating it as an edit would prune a dialog value no one replaced.
  const named = new Set(
    operation.kind === 'unset'
      ? operation.fields
      : Object.entries(operation.overrides as Record<string, unknown>)
          .filter(([, value]) => value !== undefined)
          .map(([field]) => field)
  );
  const editedWireFields = Object.entries(PRUNABLE_DATE_FIELDS)
    .filter(([overrideField]) => named.has(overrideField))
    .map(([, wireField]) => wireField);
  if (editedWireFields.length === 0) {
    return scheduleSlotOverrides;
  }
  const editedSlotIds =
    operation.kind === 'slot' || operation.kind === 'unset'
      ? [operation.slotId]
      : operation.kind === 'ids'
        ? operation.slotIds
        : null;
  if (editedSlotIds === null) {
    return scheduleSlotOverrides; // `filter` scope: ids aren't enumerable client-side.
  }
  const slotIdSet = new Set(editedSlotIds.map(String));
  const wireFieldSet = new Set(editedWireFields);
  let changed = false;
  const pruned = scheduleSlotOverrides.reduce<Array<Record<string, unknown>>>(
    (kept, op) => {
      const opOverrides = op.overrides as Record<string, unknown> | undefined;
      if (
        op.type !== 'individual' ||
        typeof op.slot_id !== 'string' ||
        !slotIdSet.has(op.slot_id) ||
        !opOverrides
      ) {
        kept.push(op);
        return kept;
      }
      const surviving = Object.entries(opOverrides).filter(
        ([field]) => !wireFieldSet.has(field)
      );
      if (surviving.length === Object.keys(opOverrides).length) {
        kept.push(op); // this op set none of the grid-owned fields — keep as-is
        return kept;
      }
      changed = true;
      if (surviving.length > 0) {
        kept.push({ ...op, overrides: Object.fromEntries(surviving) });
      }
      // else: the op only carried now-grid-owned fields → drop it entirely.
      return kept;
    },
    []
  );
  return changed ? pruned : scheduleSlotOverrides;
}

/** The working-config writes one committed override operation needs. */
export interface OverrideCommitSinks {
  /** Appends the operation to the pending override log. */
  appendSlotOverride: (operation: SlotOverrideOperation) => void;
  /** The schedule dialog's channel as it stands at commit time. */
  readScheduleSlotOverrides: () => Array<Record<string, unknown>>;
  /** Replaces the schedule dialog's channel. */
  setScheduleSlotOverrides: (ops: Array<Record<string, unknown>>) => void;
  /**
   * The operation names a composite wellbore's leg rather than a row (`targetsCompositeLeg`):
   * the leg is painted, but the row's recipe is the server's to fold, so the grids refetch.
   */
  targetsCompositeLeg?: boolean;
}

/**
 * Commits one override operation to the working config: invalidate the surfaces it touches,
 * append it to the log, and prune the schedule dialog's copy of any milestone it sets.
 *
 * ── ONE SEQUENCE FOR EVERY HAND THAT EDITS ───────────────────────────────────────────────
 *
 * The grid/tearsheet commit and the agent commit used to carry their own copies of these
 * steps, and the agent's lacked the prune — so a date the assistant staged was outranked by
 * an older schedule-dialog value for the same slot, because that channel is applied last.
 * Each caller paints its own caches first (the two paint different query keys); everything
 * after the paint is here.
 *
 * @param {QueryClient} queryClient - The page's react-query client.
 * @param {number} packageId - The package whose caches are keyed.
 * @param {SlotOverrideOperation} operation - The operation to commit.
 * @param {OverrideCommitSinks} sinks - The working-config writes.
 * @return {void}
 */
export function commitOverrideToWorkingConfig(
  queryClient: QueryClient,
  packageId: number,
  operation: SlotOverrideOperation,
  sinks: OverrideCommitSinks
): void {
  // Append before invalidating: a revert's refetch fires inside the invalidation, and it
  // must carry the operation that caused it.
  sinks.appendSlotOverride(operation);
  const current = sinks.readScheduleSlotOverrides();
  const pruned = pruneScheduleOverridesForOperation(current, operation);
  if (pruned !== current) {
    sinks.setScheduleSlotOverrides(pruned);
  }
  invalidateOverrideSurfaces(queryClient, packageId, operation, {
    targetsCompositeLeg: sinks.targetsCompositeLeg,
  });
}
